/**
 * 기관 환경설정 저장소
 * - 예산 핵심 키(finalBudgetMode 등)는 budgetTenantSettings 와 동기화
 * - 그 외 키는 cm_tenant_env_settings(API) 또는 envTenantSettings|{tenantCode}(로컬)
 * - 인사 탭 숨김은 hr.master.hiddenTabs 와 동기화
 */

import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  cacheGet,
  cacheSet,
  commonSaveList,
  commonSelect,
  parseJsonObject,
} from 'services/commonQuery';
import {
  getCachedBudgetTenantSettings,
  loadBudgetTenantSettings,
  saveBudgetTenantSettings,
  FINAL_BUDGET_MODE,
  PRIOR_YEAR_BASIS,
} from 'budget/budgetTenantSettingsStore';
import {
  loadHiddenMasterTabsFromLocal,
  MASTER_DETAIL_TAB_DEFS,
} from 'pages/hr/employeeMaster/masterDetailTabConfig';

export const ENV_SETTINGS_PREFIX = 'envTenantSettings';

const envExtraCache = new Map();

export function envSettingsKey(tenantCode) {
  return [ENV_SETTINGS_PREFIX, tenantCode || ''].join('|');
}

function normalizeLoadTabs(v) {
  if (v === 'UPSTREAM_ONLY' || v === 'BALANCE_ONLY' || v === 'BOTH') return v;
  return 'BOTH';
}

function normalizeLoadDefaultTab(v) {
  return v === 'BALANCE' ? 'BALANCE' : 'UPSTREAM';
}

function normalizeUpstreamDupMode(v) {
  if (v === 'BLOCK' || v === 'OFF') return v;
  return 'WARN';
}

/** 모듈별 기본값 (예산 linked 키는 budget 스토어가 SSOT) */
export function defaultEnvModuleValues() {
  return {
    budget: {
      amountUnit: 'thousand',
      roundPolicy: 'expenseCeilIncomeFloor',
      calcLevelMax: '5',
      defaultTreeMode: 'SUBJECT',
      maxPreSeq: 9,
      maxDeemedSeq: 100,
      monthlyBudgetLabel: '월별예산',
      monthlyDistDefault: 'month',
      changeMatchMode: 'ONE_TO_ONE',
    },
    expense: {
      obligationConfirmMode: 'APPROVE',
      accountingMode: 'MATCHED',
      allowNegativeExec: 'N',
      resolutionLoadTabs: 'BOTH',
      resolutionLoadDefaultTab: 'UPSTREAM',
      obligationLoadTabs: 'BOTH',
      obligationLoadDefaultTab: 'UPSTREAM',
      upstreamDupMode: 'WARN',
    },
    income: {
      accountingMode: 'MATCHED',
      receiptOnIncomeApprove: 'N',
    },
    master: {
      lockSearchDeptForNonMaster: true,
    },
    hr: {
      hiddenMasterTabs: [],
    },
    access: {},
  };
}

function loadExtraFromLocal(tenantCode) {
  try {
    const raw = localStorage.getItem(envSettingsKey(tenantCode));
    if (!raw) return {};
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
}

function getExtra(tenantCode) {
  const tc = tenantCode || '';
  const cached = cacheGet(envExtraCache, tc);
  if (cached && typeof cached === 'object') return cached;
  if (isBdApiEnabled()) return {};
  const local = loadExtraFromLocal(tc);
  cacheSet(envExtraCache, tc, local);
  return local;
}

/**
 * 화면용 전체 값 로드 (동기 · 캐시/로컬 기준)
 * API 모드에서는 loadEnvSettingsAsync 이후 캐시가 채워진다.
 * @param {string} tenantCode
 */
export function loadEnvSettings(tenantCode) {
  const budgetCore = getCachedBudgetTenantSettings(tenantCode);
  const extra = getExtra(tenantCode);
  const defaults = defaultEnvModuleValues();
  const hiddenTabs = loadHiddenMasterTabsFromLocal();

  const budgetExtra = {
    ...defaults.budget,
    ...(extra.budget && typeof extra.budget === 'object' ? extra.budget : {}),
  };
  const masterExtra = {
    ...defaults.master,
    ...(extra.master && typeof extra.master === 'object' ? extra.master : {}),
  };
  if (extra.budget?.lockSearchDeptForNonMaster != null && extra.master?.lockSearchDeptForNonMaster == null) {
    masterExtra.lockSearchDeptForNonMaster = !!extra.budget.lockSearchDeptForNonMaster;
  }

  return {
    budget: {
      finalBudgetMode: budgetCore.finalBudgetMode || FINAL_BUDGET_MODE.SUM_DELTA,
      priorYearBasis: budgetCore.priorYearBasis || PRIOR_YEAR_BASIS.MAIN_ONLY,
      spare1Label: budgetCore.spare1Label || '성립전',
      spare2Label: budgetCore.spare2Label || '간주예산',
      ...budgetExtra,
    },
    master: masterExtra,
    hr: {
      ...defaults.hr,
      ...(extra.hr && typeof extra.hr === 'object' ? extra.hr : {}),
      hiddenMasterTabs: Array.isArray(hiddenTabs) ? hiddenTabs : [],
    },
    access: {
      ...defaults.access,
      ...(extra.access && typeof extra.access === 'object' ? extra.access : {}),
    },
    expense: {
      ...defaults.expense,
      ...(extra.expense && typeof extra.expense === 'object' ? extra.expense : {}),
      obligationConfirmMode:
        extra.expense?.obligationConfirmMode === 'CONFIRM' ? 'CONFIRM' : 'APPROVE',
      accountingMode:
        extra.expense?.accountingMode === 'SEPARATED' ? 'SEPARATED' : 'MATCHED',
      allowNegativeExec: extra.expense?.allowNegativeExec === 'Y' ? 'Y' : 'N',
      resolutionLoadTabs: normalizeLoadTabs(extra.expense?.resolutionLoadTabs),
      resolutionLoadDefaultTab: normalizeLoadDefaultTab(extra.expense?.resolutionLoadDefaultTab),
      obligationLoadTabs: normalizeLoadTabs(extra.expense?.obligationLoadTabs),
      obligationLoadDefaultTab: normalizeLoadDefaultTab(extra.expense?.obligationLoadDefaultTab),
      upstreamDupMode: normalizeUpstreamDupMode(extra.expense?.upstreamDupMode),
    },
    income: {
      ...defaults.income,
      ...(extra.income && typeof extra.income === 'object' ? extra.income : {}),
      accountingMode:
        extra.income?.accountingMode === 'SEPARATED' ? 'SEPARATED' : 'MATCHED',
      receiptOnIncomeApprove: extra.income?.receiptOnIncomeApprove === 'Y' ? 'Y' : 'N',
    },
  };
}

/** API/로컬에서 extra + 예산 핵심을 선로딩 */
export async function loadEnvSettingsAsync(tenantCode) {
  const tc = tenantCode || '';
  await loadBudgetTenantSettings(tc);
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmTenantEnvSettings.selectSettings', { tenantCode: tc });
    const json = rows[0]?.settingsJson;
    const extra = parseJsonObject(json);
    cacheSet(envExtraCache, tc, extra);
  } else {
    cacheSet(envExtraCache, tc, loadExtraFromLocal(tc));
  }
  return loadEnvSettings(tc);
}

function saveHiddenMasterTabs(tabIds) {
  const allowed = new Set(MASTER_DETAIL_TAB_DEFS.map((t) => t.id));
  const next = (tabIds || []).map(String).filter((id) => allowed.has(id));
  localStorage.setItem('hr.master.hiddenTabs', JSON.stringify(next));
  return next;
}

/**
 * @param {string} tenantCode
 * @param {Record<string, Record<string, unknown>>} modulesValues
 */
export async function saveEnvSettings(tenantCode, modulesValues = {}) {
  const tc = tenantCode || '';
  const budget = modulesValues.budget || {};
  const expense = modulesValues.expense || {};
  const income = modulesValues.income || {};
  const master = modulesValues.master || {};
  const hr = modulesValues.hr || {};
  const access = modulesValues.access || {};

  await saveBudgetTenantSettings(tc, {
    finalBudgetMode: budget.finalBudgetMode,
    priorYearBasis: budget.priorYearBasis,
    spare1Label: String(budget.spare1Label || '성립전').trim() || '성립전',
    spare2Label: String(budget.spare2Label || '간주예산').trim() || '간주예산',
  });

  const lock = master.lockSearchDeptForNonMaster !== false;

  const extraPayload = {
    budget: {
      amountUnit: budget.amountUnit || 'thousand',
      roundPolicy: budget.roundPolicy || 'expenseCeilIncomeFloor',
      calcLevelMax: String(budget.calcLevelMax || '5'),
      defaultTreeMode: budget.defaultTreeMode || 'SUBJECT',
      maxPreSeq: Number(budget.maxPreSeq) || 9,
      maxDeemedSeq: Number(budget.maxDeemedSeq) || 100,
      monthlyBudgetLabel: String(budget.monthlyBudgetLabel || '월별예산').trim() || '월별예산',
      monthlyDistDefault: budget.monthlyDistDefault || 'month',
      changeMatchMode: budget.changeMatchMode === 'N_TO_N' ? 'N_TO_N' : 'ONE_TO_ONE',
    },
    expense: {
      obligationConfirmMode: expense.obligationConfirmMode === 'CONFIRM' ? 'CONFIRM' : 'APPROVE',
      accountingMode: expense.accountingMode === 'SEPARATED' ? 'SEPARATED' : 'MATCHED',
      allowNegativeExec: expense.allowNegativeExec === 'Y' ? 'Y' : 'N',
      resolutionLoadTabs: normalizeLoadTabs(expense.resolutionLoadTabs),
      resolutionLoadDefaultTab: normalizeLoadDefaultTab(expense.resolutionLoadDefaultTab),
      obligationLoadTabs: normalizeLoadTabs(expense.obligationLoadTabs),
      obligationLoadDefaultTab: normalizeLoadDefaultTab(expense.obligationLoadDefaultTab),
      upstreamDupMode: normalizeUpstreamDupMode(expense.upstreamDupMode),
    },
    income: {
      accountingMode: income.accountingMode === 'SEPARATED' ? 'SEPARATED' : 'MATCHED',
      receiptOnIncomeApprove: income.receiptOnIncomeApprove === 'Y' ? 'Y' : 'N',
    },
    master: {
      lockSearchDeptForNonMaster: lock,
    },
    hr: {},
    access: { ...access },
    savedAt: new Date().toISOString(),
  };

  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmTenantEnvSettings.selectSettings', { tenantCode: tc });
    await commonSaveList(
      [
        'CmTenantEnvSettings.insertSettings',
        'CmTenantEnvSettings.updateSettings',
        'CmTenantEnvSettings.updateSettings',
      ],
      [
        {
          settingsJson: JSON.stringify(extraPayload),
          mod: rows.length ? 'U' : 'A',
        },
      ],
      { tenantCode: tc }
    );
    cacheSet(envExtraCache, tc, extraPayload);
  } else {
    localStorage.setItem(envSettingsKey(tc), JSON.stringify(extraPayload));
    cacheSet(envExtraCache, tc, extraPayload);
  }

  if (Array.isArray(hr.hiddenMasterTabs)) {
    saveHiddenMasterTabs(hr.hiddenMasterTabs);
  }

  return loadEnvSettings(tc);
}
